import { differenceInHours } from 'date-fns';
import { storageHelpers } from '../../common/helpers/browser-storage';
import {
    ESRI_ALTERNATIVE_BASEMAPS,
    ESRI_BASEMAPS,
    MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY,
} from '../configurations';
import { Basemap, MapView, WebMap } from '../constructors';
import { logMapError } from '../helpers/utils';
import { incrementBasemapLoadErrorCount } from '../helpers/map-diagnostic-context';
import { isEsriAuthError } from '../esri-identity';
import type { EsriBasemap, MapViewpoint } from '../types';
import { isValidLatLng } from '../../location/helpers';
import type { GeoPoint } from '../../location/types';

/** The basemap config for a map mode, the same lookup the map mode toggle uses. */
export function findBasemapConfigForMode(
    mapMode: string | undefined,
    isAlternativeBasemap?: boolean
): EsriBasemap | undefined {
    return Object.values(isAlternativeBasemap ? ESRI_ALTERNATIVE_BASEMAPS : ESRI_BASEMAPS).find(
        (basemapInfo) => basemapInfo.mode === mapMode
    );
}

/** Whether `basemap` was built from `basemapConfig` by `createBasemapForConfig`. */
export function isBasemapForConfig(
    basemap: Basemap | null | undefined,
    basemapConfig: EsriBasemap
): boolean {
    if (!basemap) {
        return false;
    }
    return basemapConfig.isPortalItem
        ? basemap.portalItem?.id === basemapConfig.id
        : basemap.style?.id === basemapConfig.id;
}

/**
 * A copy of the viewpoint if a view can be built at it, otherwise undefined. A view that
 * isn't ready yet reports zoom -1 and may have no center, and a map built there would show
 * nothing useful.
 */
export function usableViewpoint(viewpoint: MapViewpoint | undefined): MapViewpoint | undefined {
    if (
        !viewpoint ||
        !isValidLatLng(viewpoint.center?.latitude, viewpoint.center?.longitude) ||
        !Number.isFinite(viewpoint.zoom) ||
        viewpoint.zoom < 0
    ) {
        return undefined;
    }
    return {
        center: { latitude: viewpoint.center.latitude, longitude: viewpoint.center.longitude },
        zoom: viewpoint.zoom,
    };
}

/** Where a view is looking now, as plain data, or undefined if it can't say yet. */
export function readViewpoint(view: MapView): MapViewpoint | undefined {
    return usableViewpoint({
        center: { latitude: view.center?.latitude, longitude: view.center?.longitude } as GeoPoint,
        zoom: view.zoom,
    });
}

/**
 * Constructs a Basemap for a config entry (ESRI_BASEMAPS / ESRI_ALTERNATIVE_BASEMAPS) — the
 * one place that knows how to authenticate a basemap request, so callers must go through
 * this rather than re-inlining the `isPortalItem` branch.
 *
 * Portal items authenticate via the credential already registered with IdentityManager (see
 * `register()` in esri-identity.ts), so no apiKey is attached there. Style requests attach
 * apiKey only when a token is available, matching the pre-OAuth `{ id }`-only shape.
 */
export function createBasemapForConfig(
    basemapConfig: EsriBasemap,
    getEsriToken?: () => string | undefined
): Basemap {
    if (basemapConfig.isPortalItem) {
        return new Basemap({ portalItem: { id: basemapConfig.id } });
    }
    const apiKey = getEsriToken?.();
    return new Basemap({
        style: apiKey ? { id: basemapConfig.id, apiKey } : { id: basemapConfig.id },
    });
}

/**
 * Loads `basemap`. On a credential error, calls `refreshCredential` and retries once
 * against a fresh Basemap (the SDK won't retry a failed load on the same instance); skips
 * the retry if the refresh doesn't yield a usable token. `onLoaded` gets whichever instance
 * loaded; everything else goes to `onGiveUp`.
 */
export function loadBasemapWithAuthRetry({
    basemap,
    createRetryBasemap,
    refreshCredential,
    onLoaded,
    onGiveUp,
}: {
    basemap: Basemap;
    createRetryBasemap: () => Basemap;
    refreshCredential: (() => Promise<boolean>) | undefined;
    onLoaded: (loadedBasemap: Basemap) => void;
    onGiveUp: (error: unknown) => void;
}): void {
    basemap
        .load()
        .then(() => onLoaded(basemap))
        .catch((error) => {
            if (!refreshCredential || !isEsriAuthError(error)) {
                onGiveUp(error);
                return;
            }
            refreshCredential()
                .then((refreshed) => {
                    if (!refreshed) {
                        onGiveUp(error);
                        return;
                    }
                    const retryBasemap = createRetryBasemap();
                    return retryBasemap.load().then(() => onLoaded(retryBasemap));
                })
                .catch(onGiveUp);
        });
}

/**
 * Whether `error` is `CONT_0001`: the portal item does not exist or this credential cannot
 * see it. The code is on the request error that the WebMap error wraps.
 */
export function isPortalItemUnavailableError(error: unknown): boolean {
    let current = error as { details?: { messageCode?: string; error?: unknown } } | undefined;
    while (current) {
        if (current.details?.messageCode === 'CONT_0001') {
            return true;
        }
        current = current.details?.error as typeof current;
    }
    return false;
}

/**
 * How a portal webmap load ended:
 * - `loaded`: use `webmap`.
 * - `unavailable`: missing or inaccessible item. Use the default basemap.
 * - `timedOut`: no answer in time. Use the default basemap.
 * - `failed`: any other error. Use `webmap`; the map shows its error state.
 */
export type PortalWebMapLoadResponse = {
    webmapPortalId: string;
    webmap: WebMap;
    status: 'loaded' | 'unavailable' | 'timedOut' | 'failed';
};

/** How long to wait for the portal webmap before using the default basemap. */
export const PORTAL_WEBMAP_LOAD_TIMEOUT_MS = 30_000;

/**
 * Loads a portal webmap before a view is built on it. Cancelled after
 * `PORTAL_WEBMAP_LOAD_TIMEOUT_MS`; any failure is logged as `portal_load_error`.
 */
export async function loadPortalWebMap({
    webmapPortalId,
    isAlternativeBasemap,
}: {
    webmapPortalId: string;
    isAlternativeBasemap?: boolean;
}): Promise<PortalWebMapLoadResponse> {
    const webmap = new WebMap({
        portalItem: {
            id: webmapPortalId,
        },
    });

    let isTimedOut = false;
    const timeoutId = setTimeout(() => {
        isTimedOut = true;
        // cancelLoad exists at runtime but is missing from this SDK version's typings.
        (webmap as WebMap & Pick<__esri.Loadable, 'cancelLoad'>).cancelLoad();
    }, PORTAL_WEBMAP_LOAD_TIMEOUT_MS);

    try {
        await webmap.load();
        return { webmapPortalId, webmap, status: 'loaded' };
    } catch (error) {
        const status = isTimedOut
            ? 'timedOut'
            : isPortalItemUnavailableError(error)
              ? 'unavailable'
              : 'failed';

        logMapError({
            source: 'service',
            event: 'portal_load_error',
            error: error as Error,
            extraData: { webmapPortalId, isAlternativeBasemap, status },
        });

        return { webmapPortalId, webmap, status };
    } finally {
        clearTimeout(timeoutId);
    }
}

/**
 * Puts the basemap for the map mode into a department webmap that has loaded but has no view
 * yet. The map mode toggle replaces a webmap's own basemap with that one as soon as a view
 * exists, so swapping it in first means the webmap's own basemap is never downloaded. If the
 * mode's basemap can't load, the webmap's own goes back: that is what the toggle leaves on
 * screen when its swap fails.
 */
function swapInBasemapForMode({
    webmap,
    basemapConfig,
    webmapPortalId,
    isAlternativeBasemap,
    isEmbeddedBasemap,
    setMapLoadFailed,
    refreshCredential,
    getEsriToken,
}: {
    webmap: WebMap;
    basemapConfig: EsriBasemap;
    webmapPortalId: string;
    isAlternativeBasemap?: boolean;
    isEmbeddedBasemap?: boolean;
    setMapLoadFailed?: (failed: boolean) => void;
    refreshCredential?: () => Promise<boolean>;
    getEsriToken?: () => string | undefined;
}): { webmap: WebMap; cancelPendingLoadEffects: () => void } {
    const ownBasemap = webmap.basemap;
    const createBasemap = () => createBasemapForConfig(basemapConfig, getEsriToken);
    const basemap = createBasemap();
    webmap.basemap = basemap;

    let isLoadStale = false;

    loadBasemapWithAuthRetry({
        basemap,
        createRetryBasemap: createBasemap,
        refreshCredential,
        onLoaded: (loadedBasemap) => {
            // The original is already in the webmap; only a retry needs swapping in.
            if (!isLoadStale && loadedBasemap !== basemap) {
                webmap.basemap = loadedBasemap;
            }
        },
        onGiveUp: (error) => {
            if (!isLoadStale) {
                webmap.basemap = ownBasemap;
                setMapLoadFailed?.(true);
            }

            incrementBasemapLoadErrorCount();
            logMapError({
                source: 'service',
                event: 'basemap_load_error',
                error: error as Error,
                isEmbeddedMap: isEmbeddedBasemap,
                extraData: {
                    webmapPortalId,
                    isAlternativeBasemap,
                    isStale: isLoadStale,
                },
            });
        },
    });

    return {
        webmap,
        cancelPendingLoadEffects: () => {
            isLoadStale = true;
        },
    };
}

/**
 * Builds the initial WebMap for a map view.
 *
 * - `portalWebMapLoadResponse` loaded or failed: uses its WebMap, failing the map if it failed.
 *   With `useBasemapForMode`, a loaded one starts on the basemap for `mapMode` instead of its
 *   own (`swapInBasemapForMode`).
 * - `portalWebMapLoadResponse` unavailable or timed out: builds the default basemap below.
 * - `webmapPortalId` alone: loads a portal-scoped WebMap. A load failure is never retried.
 * - otherwise: builds a WebMap around a basemap from ESRI_BASEMAPS / ESRI_ALTERNATIVE_BASEMAPS
 *   (via `createBasemapForConfig`), retrying once through `loadBasemapWithAuthRetry` when
 *   `refreshCredential` is given.
 *
 * Returns `cancelPendingLoadEffects` for the caller to invoke on teardown (e.g. view
 * disposal). It doesn't abort the in-flight load/retry — the SDK has no such hook — it only
 * stops a load that resolves afterward from touching state (`setMapLoadFailed`,
 * `webmap.basemap`). A failure is still logged either way, tagged `isStale`.
 */
export const createInitialWebMap = ({
    webmapPortalId,
    portalWebMapLoadResponse,
    isAlternativeBasemap,
    setMapLoadFailed,
    isEmbeddedBasemap,
    mapMode,
    useBasemapForMode,
    refreshCredential,
    getEsriToken,
}: {
    webmapPortalId?: string;
    /** The result of `loadPortalWebMap`, when the portal webmap was loaded up front. */
    portalWebMapLoadResponse?: PortalWebMapLoadResponse;
    isAlternativeBasemap?: boolean;
    setMapLoadFailed?: (failed: boolean) => void;
    isEmbeddedBasemap?: boolean;
    mapMode?: string;
    /**
     * Start on the basemap for `mapMode` itself, as the map mode toggle would pick it,
     * rather than dark or light. Without it, a satellite-mode map starts on light and the
     * toggle swaps in the right basemap afterward, downloading two. A loaded portal webmap's
     * own basemap is replaced the same way.
     */
    useBasemapForMode?: boolean;
    /** Retries a basemap load once via a credential refresh. Omit to fail immediately, e.g. for callers outside EsriConfiguration's subtree. Not used for a portal webmap's own basemap. */
    refreshCredential?: () => Promise<boolean>;
    /** Current Esri OAuth token for basemap style requests. Omit to rely on the legacy static apiKey only. */
    getEsriToken?: () => string | undefined;
}): { webmap: WebMap; cancelPendingLoadEffects: () => void } => {
    if (portalWebMapLoadResponse?.status === 'loaded') {
        const basemapConfigForMode = useBasemapForMode
            ? findBasemapConfigForMode(mapMode, isAlternativeBasemap)
            : undefined;
        if (basemapConfigForMode) {
            return swapInBasemapForMode({
                webmap: portalWebMapLoadResponse.webmap,
                basemapConfig: basemapConfigForMode,
                webmapPortalId: portalWebMapLoadResponse.webmapPortalId,
                isAlternativeBasemap,
                isEmbeddedBasemap,
                setMapLoadFailed,
                refreshCredential,
                getEsriToken,
            });
        }
        return { webmap: portalWebMapLoadResponse.webmap, cancelPendingLoadEffects: () => {} };
    }

    if (portalWebMapLoadResponse?.status === 'failed') {
        setMapLoadFailed?.(true);
        return { webmap: portalWebMapLoadResponse.webmap, cancelPendingLoadEffects: () => {} };
    }

    if (webmapPortalId && !portalWebMapLoadResponse) {
        const webmap = new WebMap({
            portalItem: {
                id: webmapPortalId,
            },
        });

        let isPortalLoadStale = false;

        webmap.load().catch((error) => {
            if (error && !isPortalLoadStale) {
                setMapLoadFailed?.(true);
            }

            logMapError({
                source: 'service',
                event: 'portal_load_error',
                error,
                isEmbeddedMap: isEmbeddedBasemap,
                extraData: {
                    webmapPortalId,
                    isAlternativeBasemap,
                    isStale: isPortalLoadStale,
                },
            });
        });

        return {
            webmap,
            cancelPendingLoadEffects: () => {
                isPortalLoadStale = true;
            },
        };
    }

    // No webmap, or an unavailable or timed-out one: the default basemap.
    const baseMapType = isAlternativeBasemap ? ESRI_ALTERNATIVE_BASEMAPS : ESRI_BASEMAPS;
    const basemapStyle =
        (useBasemapForMode && findBasemapConfigForMode(mapMode, isAlternativeBasemap)) ||
        (mapMode === 'DARK_MODE' ? baseMapType.DARK : baseMapType.LIGHT);

    const createBasemap = () => createBasemapForConfig(basemapStyle, getEsriToken);

    const basemap = createBasemap();
    const webmap = new WebMap({ basemap });

    let isLoadStale = false;

    loadBasemapWithAuthRetry({
        basemap,
        createRetryBasemap: createBasemap,
        refreshCredential,
        onLoaded: (loadedBasemap) => {
            if (isLoadStale) {
                return;
            }
            // The original is already wired in above; only a retry needs swapping in.
            if (loadedBasemap !== basemap) {
                webmap.basemap = loadedBasemap;
            }
        },
        onGiveUp: (error) => {
            if (error && !isLoadStale) {
                setMapLoadFailed?.(true);
            }

            incrementBasemapLoadErrorCount();
            logMapError({
                source: 'service',
                event: 'basemap_load_error',
                error: error as Error,
                isEmbeddedMap: isEmbeddedBasemap,
                extraData: {
                    webmapPortalId,
                    isAlternativeBasemap,
                    isStale: isLoadStale,
                },
            });
        },
    });

    return {
        webmap,
        cancelPendingLoadEffects: () => {
            isLoadStale = true;
        },
    };
};

/**
 * Swaps `mapView`'s basemap to the one built from `basemapConfig`, retrying once via a
 * credential refresh on an auth failure — the same retry semantics as `createInitialWebMap`,
 * but reassigning onto an already-mounted view instead of building a new WebMap.
 *
 * Returns `cancelPendingLoadEffects` for the caller to invoke when this load is superseded
 * (e.g. a newer mode toggle). Same semantics as `createInitialWebMap`'s: it doesn't abort
 * the in-flight load/retry, it only stops a load that resolves afterward from touching
 * state (`setMapLoadFailed`, `mapView.map.basemap`). A failure is still logged either way,
 * tagged `isStale`.
 */
export function loadBasemapForMode({
    mapView,
    basemapConfig,
    isAlternativeBasemap,
    setMapLoadFailed,
    refreshCredential,
    getEsriToken,
}: {
    mapView: MapView;
    basemapConfig: EsriBasemap;
    isAlternativeBasemap?: boolean;
    setMapLoadFailed?: (failed: boolean) => void;
    refreshCredential?: () => Promise<boolean>;
    getEsriToken?: () => string | undefined;
}): { cancelPendingLoadEffects: () => void } {
    let isLoadStale = false;

    try {
        const createBasemap = () => createBasemapForConfig(basemapConfig, getEsriToken);

        loadBasemapWithAuthRetry({
            basemap: createBasemap(),
            createRetryBasemap: createBasemap,
            refreshCredential,
            onLoaded: (loadedBasemap) => {
                if (!isLoadStale) {
                    mapView.map.basemap = loadedBasemap;
                }
            },
            onGiveUp: (error) => {
                if (!isLoadStale) {
                    setMapLoadFailed?.(true);
                }
                const basemapError = error as Error;
                logMapError({
                    source: 'service',
                    event: isEsriAuthError(basemapError)
                        ? 'token_invalid'
                        : 'basemap_toggle_load_error',
                    error: basemapError,
                    extraData: {
                        chosenBasemapConfiguration: basemapConfig,
                        isAlternativeBasemap,
                        isStale: isLoadStale,
                    },
                });
            },
        });
    } catch (error) {
        setMapLoadFailed?.(true);
        logMapError({
            source: 'service',
            event: 'basemap_toggle_error',
            error: error as Error,
            extraData: {
                chosenBasemapConfiguration: basemapConfig,
                isAlternativeBasemap,
            },
        });
    }

    return {
        cancelPendingLoadEffects: () => {
            isLoadStale = true;
        },
    };
}

export const shouldLoadAlternativeBasemap = () => {
    let isAlternativeBasemap = false;
    const alternativeBasemapActivationDate = storageHelpers.getItem<string>(
        MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY
    );
    if (alternativeBasemapActivationDate) {
        const alternativeBasemapActivationExpired =
            differenceInHours(new Date(), new Date(alternativeBasemapActivationDate)) > 16;
        if (alternativeBasemapActivationExpired) {
            storageHelpers.removeItem(MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY);
        } else {
            isAlternativeBasemap = true;
        }
    }
    return isAlternativeBasemap;
};
